import { useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { useEntitlements } from '@/lib/queries';
import { useSession } from '@/store/session';
import { useResults } from '@/store/results';
import { CAPABILITY_SCOPES } from '@/auth/scopes';
import { auditLicenses, type LicenseResult } from '@/features/licenses/licenses';
import { Panel } from '@/components/Panel';
import { ModuleGate } from '@/components/ModuleGate';
import { Metric, MetricGrid } from '@/components/Metric';
import { StateBadge } from '@/components/StateBadge';
import { ErrorNotice } from '@/components/ErrorNotice';
import { IconGrid } from '@/components/icons';
import { readToken } from '@/lib/theme';

type Status = 'idle' | 'running' | 'done' | 'error';

/**
 * Combined "Licensing & entitlements" section.
 *   • Entitlements (C2) — which capabilities the tenant's SKUs actually grant.
 *   • Licence allocation (C11) — overlapping service plans and redundant SKUs.
 * Two independently-consented sub-views under one heading.
 */
export function LicensingPanel() {
  // -- Entitlements (C2) --
  const c2 = useSession((s) => s.hasConsent('C2'));
  const { data: entitlements, isLoading: entLoading, isError: entIsError, error: entError, refetch } =
    useEntitlements(c2);

  // -- Licence allocation (C11) --
  const setLicenses = useResults((s) => s.setLicenses);
  const [status, setStatus] = useState<Status>('idle');
  const [result, setResult] = useState<LicenseResult | null>(null);
  const [error, setError] = useState<unknown>(null);

  const accent = readToken('--accent', '#5C7E8F');
  const grid = readToken('--border', '#D4DDE2');
  const text3 = readToken('--text-3', '#6E7478');

  async function runLicences() {
    setStatus('running');
    setError(null);
    try {
      const res = await auditLicenses();
      setResult(res);
      setLicenses(res);
      setStatus('done');
    } catch (e) {
      setError(e);
      setStatus('error');
    }
  }

  return (
    <Panel
      id="licensing"
      icon={<IconGrid />}
      title="Licensing & entitlements"
      cap="C2 · C11"
      why="What your tenant's SKUs actually entitle, and where licences overlap or are redundant. Read-only, per-module consent."
      action={
        c2 ? (
          <button className="btn btn--secondary btn--sm" onClick={() => void refetch()}>Refresh entitlements</button>
        ) : undefined
      }
    >
      {/* ---- Entitlements ---- */}
      <h3 className="metric__label">Entitlement matrix</h3>
      <ModuleGate cap="C2">
        {entLoading && <p className="panel__loading">Reading tenant SKUs…</p>}
        {entIsError && <ErrorNotice error={entError} />}
        {entitlements && (
          <table className="matrix">
            <thead>
              <tr><th>Capability</th><th>Status</th><th>Detail</th></tr>
            </thead>
            <tbody>
              {entitlements.map((cap) => (
                <tr key={cap.id}>
                  <td>
                    <div className="matrix__label">{cap.label}</div>
                    <div className="matrix__desc">{cap.description}</div>
                  </td>
                  <td><StateBadge state={cap.state} /></td>
                  <td className="matrix__detail">{cap.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ModuleGate>

      {/* ---- Licence allocation ---- */}
      <h3 className="metric__label" style={{ marginTop: 22 }}>Licence allocation</h3>
      <p className="panel__why" style={{ marginTop: 0 }}>{CAPABILITY_SCOPES.C11.why}</p>
      <ModuleGate cap="C11">
        <div className="gate__cta">
          <button className="btn btn--primary" onClick={() => void runLicences()} disabled={status === 'running'}>
            {status === 'running' ? 'Analysing licences…' : result ? 'Re-analyse' : 'Analyse licence allocation'}
          </button>
        </div>

        {status === 'error' && <ErrorNotice error={error} />}

        {result && (
          <>
            <MetricGrid>
              <Metric label="Licensed users" value={result.totalLicensedUsers} />
              <Metric label="Overlapping plans" value={result.usersWithOverlap}
                tone={result.usersWithOverlap > 0 ? 'red' : 'green'} sub="Users w/ a plan from 2+ SKUs" />
              <Metric label="Redundant SKUs" value={result.usersWithRedundant}
                tone={result.usersWithRedundant > 0 ? 'red' : 'green'} sub="Users w/ a subset SKU" />
              <Metric label="Duplicated grants" value={result.wastedAssignments}
                tone={result.wastedAssignments > 0 ? 'red' : 'green'} sub="Extra plan assignments" />
            </MetricGrid>

            {result.topPlans.length > 0 && (
              <>
                <h3 className="metric__label" style={{ marginTop: 8 }}>Most-duplicated service plans</h3>
                <div className="chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={result.topPlans} layout="vertical" margin={{ top: 4, right: 12, bottom: 4, left: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: text3 }}
                        tickLine={false} axisLine={{ stroke: grid }} />
                      <YAxis type="category" dataKey="name" width={150}
                        tick={{ fontSize: 11, fill: text3 }} tickLine={false} axisLine={false} />
                      <Tooltip cursor={{ fill: readToken('--bg-2', '#EFF3F6') }}
                        contentStyle={{ borderRadius: 10, border: `1px solid ${grid}`, fontFamily: 'var(--font-mono)', fontSize: 12 }} />
                      <Bar dataKey="users" name="Users affected" fill={accent} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}

            {result.findings.length === 0 ? (
              <div className="notice notice--info">
                <strong>No licence overlap or redundancy found</strong>
                <p>Every licensed user's SKUs grant distinct service plans. Re-run after licence changes.</p>
              </div>
            ) : (
              <>
                <h3 className="metric__label" style={{ marginTop: 8 }}>Findings — {result.findings.length} users</h3>
                <table className="matrix">
                  <thead>
                    <tr><th>User</th><th>SKUs</th><th>Overlapping plans</th><th>Redundant SKUs</th></tr>
                  </thead>
                  <tbody>
                    {result.findings.slice(0, 100).map((f) => (
                      <tr key={f.id}>
                        <td className="matrix__label">
                          {f.displayName}
                          <span className="small muted" style={{ display: 'block' }}>{f.userPrincipalName}</span>
                        </td>
                        <td className="matrix__detail"><code className="small">{f.skus.join(', ')}</code></td>
                        <td className="matrix__detail">
                          {f.overlaps.length === 0 ? <span className="muted">—</span> :
                            f.overlaps.map((o) => (
                              <div key={o.planName}>
                                <span className="chip chip--warn">{o.planName}</span>{' '}
                                <span className="small muted">via {o.skus.join(' + ')}</span>
                              </div>
                            ))}
                        </td>
                        <td className="matrix__detail">
                          {f.redundant.length === 0 ? <span className="muted">—</span> :
                            f.redundant.map((r, i) => (
                              <div key={i}>
                                <code className="small">{r.redundant}</code>{' '}
                                <span className="small muted">covered by {r.coveredBy}</span>
                              </div>
                            ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
      </ModuleGate>
    </Panel>
  );
}
